import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, X } from "lucide-react";
import { fetchPaymentStatus } from "@/lib/api";
import { useSiteData } from "@/context/SiteData";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [info, setInfo] = useState(null);
  const { refetch } = useSiteData() || {};

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let tries = 0;
    let active = true;
    const poll = async () => {
      try {
        const d = await fetchPaymentStatus(sessionId);
        if (!active) return;
        setInfo(d);
        if (d.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(d.payment_status)) {
          setState("failed");
          return;
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          if (active) setState("failed");
          return;
        }
      }
      tries += 1;
      if (tries > 12) {
        if (active) setState("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId]);

  return (
    <section className="divine-rays relative flex min-h-[70vh] items-center overflow-hidden py-20" data-testid="payment-success-page">
      <div className="mx-auto max-w-xl px-4 text-center sm:px-6">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#B89759]" />
            <p className="mt-6 font-serif text-2xl font-light text-[#1C1815]">Confirming your payment…</p>
          </div>
        )}
        {state === "paid" && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="payment-paid">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#F4EFE6]">
              <Check className="h-9 w-9 text-[#9A7A3F]" />
            </div>
            <h1 className="mt-8 font-serif text-4xl font-light text-[#1C1815]">Thank you!</h1>
            <p className="mx-auto mt-4 max-w-md text-base font-light leading-relaxed text-[#4A423A]">
              Your payment{info?.name ? ` for ${info.name}` : ""} was received. Natalia will be in touch
              with next steps and shipping details.
            </p>
            <Link to="/" className="btn-primary mt-10" data-testid="payment-home-button">Return Home</Link>
          </motion.div>
        )}
        {state === "failed" && (
          <div data-testid="payment-failed">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#F4EFE6]">
              <X className="h-9 w-9 text-[#9A7A3F]" />
            </div>
            <h1 className="mt-8 font-serif text-4xl font-light text-[#1C1815]">Payment not completed</h1>
            <p className="mx-auto mt-4 max-w-md text-base font-light leading-relaxed text-[#4A423A]">
              We couldn't confirm your payment. If you were charged, please contact us and we'll sort it out.
            </p>
            <Link to="/portfolio" className="btn-outline mt-10" data-testid="payment-back-button">Back to Gallery</Link>
          </div>
        )}
      </div>
    </section>
  );
}
